import React, { useEffect, useRef, useState } from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import {
  Avatar,
  Badge,
  Button,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { Box, Stack } from "@mui/system";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  createDoctor,
  getDoctorByID,
  getLanguages,
  getTitles,
  updateDoctor,
} from "../../../Api/Doctor/Doctor.Api";
import { GetDoctorSpecialization } from "../../../Api/Signin/Dashboard.Api";
import { ReactComponent as ArrowDown } from "../../../Assets/Images/ArrowDown.svg";
import { ReactComponent as CameraIcon1 } from "../../../Assets/Images/CameraSvg.svg";
import { ReactComponent as Info } from "../../../Assets/Images/Info2.svg";
import{ ReactComponent as InfoIconGrey } from "../../../Assets/Images/InfoIconGrey.svg";  
import SignPlaceholder from "../../../Assets/Images/Sign.svg";
import { useAuth } from "../../../Store/AuthContext";
import {
  isUserNameDuplicateError,
  USER_NAME_ALREADY_EXISTS_VALIDATION_KEY,
} from "../../../Utils/HandleError/userNameDuplicateError";
import { validateUsername } from "../../../Utils/validateUsername";

import useLoadingState from "../../../Utils/Hooks/useLodingState";
import useOverlayState from "../../../Utils/Hooks/useOverlayState";
import { SideDrawer } from "../../atoms/Drawer/SideDrawer";
import ConsultationFeeTable from "../../molecules/ConsultationFeeTable/ConsultationFeeTable";
import { ValidationPopper } from "../../molecules/ValidationPopper/ValidationPopper";
import {
  Multicomplete,
  TagsAutocomplete,
} from "../../atoms/Input/AutoComplete";
import { PhoneNumber } from "../../atoms/Input/PhoneNumber";
import { TextArea2 } from "../../atoms/Input/TextArea";
import { TextInput } from "../../atoms/Input/TextInput";
import { LoadingButtonType } from "../../atoms/LoadingButton/LoadingButton";
import { NoRecordsFound } from "../../atoms/NoRecordsFound/NoRecordsFound";
import {
  SubHeader2,
  SubHeader6,
} from "../../atoms/Typography/SubHeader/SubHeader";
import UserAvatar from "../../atoms/UserAvatar/UserAvatar";
import { FormItemsWrap } from "../../molecules/Wrap/Wrap";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import { schema } from "./shema";

// Profile Picture Upload Component
const ProfilePictureUpload = ({ control, setValue, watch, setError }) => {
  const [pictureError, setPictureError] = useState(null);
  const fileInputRef = useRef(null);

  const profilePicture = watch("profileImage");
  const previewUrl =
    profilePicture && typeof profilePicture === "object"
      ? URL.createObjectURL(profilePicture)
      : watch("profileImage");

  const handlePictureUpload = (e) => {
    setPictureError(null);

    if (e.target.files && e.target.files[0]) {
      // Validate file is an image
      if (!e.target.files[0].type.startsWith("image/")) {
        setPictureError("Invalid file type or file size. Please try again.");
        return;
      }

      // Validate file size (5MB max)
      if (e.target.files[0].size > 5 * 1024 * 1024) {
        setPictureError("Invalid file type or file size. Please try again.");
        return;
      }

      // Set the profile picture in your form
      setValue("profileImage", e.target.files[0]);
    }
  };

  const handleRemovePicture = () => {
    setValue("profilePicture", null);
    setValue("profileImage", null);
    setPictureError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Grid container direction="row" alignItems="center" spacing={6}>
      <Grid item>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            sx={{ zIndex: 0 }}
            badgeContent={
              <IconButton
                style={{
                  padding: "11px 8px",
                  borderRadius: "24px",
                  background: "#FFFFFF",
                  border: "1px solid #D1D1D6",
                }}
                component="label"
                htmlFor="profile-picture-upload"
              >
                <CameraIcon1
                  style={{
                    width: "20px",
                  }}
                />
                <input
                  id="profile-picture-upload"
                  type="file"
                  accept="image/png, image/jpg, image/jpeg, image/heif, image/webp"
                  style={{ display: "none" }}
                  onChange={handlePictureUpload}
                  ref={fileInputRef}
                />
              </IconButton>
            }
          >
            <Box
              sx={{
                position: "relative",
                display: "inline-block",
                overflow: "visible",
              }}
            >
              <UserAvatar dimention="141px" src={previewUrl} />
              {profilePicture && (
                <Box
                  sx={{
                    position: "absolute",
                    bottom: "0",
                    width: "141px",
                    height: "47px",
                    left: "0",
                    zIndex: 2,
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    onClick={handleRemovePicture}
                    sx={{
                      fontSize: "12px",
                      color: "#FFFFFF",
                      backgroundColor: "rgba(36, 36, 36, 0.7)",
                      width: "141px",
                      height: "141px",
                      position: "absolute",
                      bottom: "0",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "flex-end",
                      justifyContent: "center",
                      cursor: "pointer",
                      textAlign: "center",
                      paddingBottom: "14px",
                      "&:hover": {
                        backgroundColor: "rgba(25, 25, 25, 0.7)",
                      },
                    }}
                  >
                    Remove
                  </Box>
                </Box>
              )}
            </Box>
          </Badge>
        </Box>
      </Grid>
      <Grid item>
        <div style={{ margin: "4px 0", fontSize: "12px", color: "#6B7280" }}>
          File types: <b>.png, .jpeg, .jpg, .gif, .heif, .webp</b>
        </div>
        <div style={{ margin: "4px 0", fontSize: "12px", color: "#6B7280" }}>
          File size: <b>5MB</b>
        </div>
        {pictureError && (
          <div style={{ color: "#FF3B30", fontSize: "12px", marginTop: "8px" }}>
            {pictureError}
          </div>
        )}
      </Grid>
    </Grid>
  );
};

// Signature Upload Component
export const SignatureUpload = ({ control, setValue, watch, setError }) => {
  const [signatureError, setSignatureError] = useState(null);
  const fileInputRef = useRef(null);
  const signatureFile = watch("signature");

  const signature = watch("signature");
  const previewUrl =
    signature && typeof signature === "object"
      ? URL.createObjectURL(signature)
      : watch("signature");

  const handleSignatureUpload = (e) => {
    setSignatureError(null);
    const file = e.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      const error = "Invalid file type or file size. Please try again.";
      setSignatureError(error);
      setError?.("signature", { type: "manual", message: error });
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      const error = "Invalid file type or file size. Please try again.";
      setSignatureError(error);
      setError?.("signature", { type: "manual", message: error });
      return;
    }

    setValue("signature", file);
    setSignatureError(null);
    setError?.("signature", null);
  };

  return (
    <Stack spacing={1} width="100%">
      <SubHeader6 text={"Signature"} />

      <label htmlFor="signature-upload" style={{ cursor: "pointer" }}>
        <Avatar
          style={{
            width: "100%",
            height: "120px",
            borderRadius: "8px",
            backgroundColor: "#E5F2FF",
            border: "0px dashed #94bfff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          variant="square"
        >
          {signatureFile ? (
            <img
              src={previewUrl}
              alt="Signature preview"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
            />
          ) : (
            <img
              src={SignPlaceholder}
              alt="Signature placeholder"
              style={{
                height: "80px",
              }}
            />
          )}
        </Avatar>
      </label>

      <input
        id="signature-upload"
        ref={fileInputRef}
        type="file"
        hidden
        accept="image/png, image/jpg, image/jpeg, image/gif, image/heif, image/webp"
        onChange={handleSignatureUpload}
      />

      <div
        style={{
          fontSize: "12px",
          color: "#6B7280",
          textAlign: "center",
          marginTop: "8px",
        }}
      >
        <div>
          File types: <b>.png, .jpeg, .jpg, .heif, .webp</b>
        </div>
        <div>
          File size: <b>2MB</b>
        </div>
      </div>

      {signatureError && (
        <div style={{ color: "#FF3B30", fontSize: "12px", marginTop: "8px" }}>
          {signatureError}
        </div>
      )}
    </Stack>
  );
};

export const AddDoctor = ({
  close,
  closeMain,
  data,
  OpenDrawer,
  loadDoctors,
  mode,
}) => {
  const { state } = useAuth();
  const [errors, setErrors] = useState({
    visible: false,
    type: undefined,
  });
  const { t } = useTranslation();

  // Add these new state variables for titles and languages
  const [languageList, setLanguageList] = useState([]);
  const [titleList, setTitleList] = useState([]);

  // Username field focus state
  const [isUsernameFocused, setIsUsernameFocused] = useState(false);
  const [isInfoIconClicked, setIsInfoIconClicked] = useState(false);
  const [isUsernameInvalid, setIsUsernameInvalid] = useState(false);

  const [originalSpecialtiesState, setOriginalSpecialtiesState] = useState({
    specialties: [],
    rates: {}
  });

  const { LoadingState, startLoading, stopLoading } = useLoadingState();

  const { closeOverlay, OverlayState, openOverlay } = useOverlayState();
  const [doctorMasterData, setDoctorMasterData] = useState({
    selectedSpeciality: null,
    data: [],
    hourRate: 0,
    onsiteHourRate: 0,
  });
  const [appType, setAppType] = useState("doctor");

  const { control, setValue, watch, setError, reset, handleSubmit, unregister } = useForm({
    defaultValues: {
      profilePicture: null, // Added for profile picture
      title: [], // Added for title
      firstName: "",
      lastName: "",
      userName: "",
      email: "",
      phoneNumber: "",
      speciality: null,
      street01: "",
      street02: "",
      city: "",
      region: "",
      state: "",
      postCode: "",
      country: "",
      registrationNo: "",
      university: [],
      SpecialityNumber: [],
      professionalLiceneseNumber: "",
      universitySpeciality: [],
      verifyDoctor: false,
      isVerfied: false,
      onsiteHourlyRate: "",
      hourlyRate: "",
      language: "EN",
      languages: [], // Added for multiple languages
      phoneNumber: "",
      phoneNumberCountryCode: "lk",
      phoneNumberDialCode: "",
      tagline: "",
      biography: "",
      experience: "",
      specialityRates: {},
      treatment: [],
      signature: null, // Added for signature
      currencyCode: "LKR",
    },
    mode: "onChange",
    resolver: yupResolver(schema(doctorMasterData.hourRate, doctorMasterData.onsiteHourRate, t, mode)),
    criteriaMode: "all",
    reValidateMode: "onChange",
  });

  const currentUserName = watch("userName") || "";
  const isUsernameValid = validateUsername(currentUserName);

  const showUsernameRules = isInfoIconClicked || (isUsernameFocused && !isUsernameValid);

  const getDoctorsByID = async () => {
  try {
    startLoading({ btnName: "default" });
    const response = await getDoctorByID({
      id: data.id,
      lang: state.lang.code,
    });

    if (response.type == 0) {
      const formattedRates = {};
      let specialityArray = response.data?.speciality || [];
        if (typeof specialityArray === 'string') {
          try {
            specialityArray = JSON.parse(specialityArray);
          } catch (e) {
            console.error('Failed to parse speciality JSON:', e);
            specialityArray = [];
          }
        }

      if (Array.isArray(specialityArray)) {
        specialityArray.forEach((spec) => {
          const specId = spec.id || spec.typeId;
          
          formattedRates[specId] = {
            onlinePrice: spec.onlineRate ?? spec.OnlineRate ?? "",
            physicalPrice: spec.onsiteRate ?? spec.OnsiteRate ?? "",
            patientOnlinePrice: spec.onlinePatientRate ?? spec.OnlinePatientRate ?? "",
            patientPhysicalPrice: spec.onsitePatientRate ?? spec.OnsitePatientRate ?? ""
          };
        });
      }

      const rawSpeciality = response.data?.speciality;
      const specialityArrayToMap = Array.isArray(rawSpeciality) ? rawSpeciality : [];

      const normalizedSpecialities = specialityArrayToMap.map(spec => ({
    ...spec,
    id: spec.id || spec.typeId
}));


        setOriginalSpecialtiesState({
          specialties: JSON.parse(JSON.stringify(normalizedSpecialities)),
          rates: JSON.parse(JSON.stringify(formattedRates))
        });

      reset({
        ...response.data,
        specialityRates: formattedRates, // Use transformed rates
        profilePicture: response.data?.profileImage || null,
        signature: response.data?.signatureImage || null,
        title: response.data?.doctorTitle || "",
        userName: response.data?.userName || response.data?.UserName || "",
        isVerfied: response.data?.verifyDoctor,
        speciality: normalizedSpecialities,
        hourlyRate: response.data?.hourlyRate <= 0 ? "" : response.data.hourlyRate,
        onsiteHourlyRate: response.data?.onsiteHourlyRate <= 0 ? "" : response.data.onsiteHourlyRate,
        currencyCode: response.data?.currencyCode || normalizedSpecialities[0]?.currencyCode || "LKR",
        professionalLiceneseNumber: response.data.professionalLiceneseNumber,
        university: response.data?.university
          ? universityHandler(response.data?.university)
          : response.data?.university,
        SpecialityNumber: response.data?.specialityNumber
          ? specialityNumberHandler(response?.data?.specialityNumber)
          : response?.data?.specialityNumber,
        universitySpeciality: response.data?.universitySpeciality
          ? qualificationHandler(response.data?.universitySpeciality)
          : response.data?.universitySpeciality,
        street01: response.data?.street1,
        street02: response.data?.street2,
        languages: response.data?.doctorLanguages || [],
        treatment: response.data?.doctorTreatments
          ? response.data.doctorTreatments.map((t) => t.treatmentInterest)
          : [],
        specialityNumber: undefined,
      });
      
      setDoctorMasterData((prev) => {
        return { ...prev, selectedSpeciality: normalizedSpecialities[0] };
      });
    
    }
    stopLoading({ btnName: "default" });
  } catch {
    return;
  }
};

  const updateProfileHandler = async (values) => {
    if (errors.visible) setErrors({ visible: false, type: undefined });

    const currentSpecialties = [...(values.speciality || [])];
    const currentRates = JSON.parse(JSON.stringify(values.specialityRates || {}));

    const selectedSpecialtyIds = currentSpecialties.map(s => s.id || s.typeId);
    const filteredRates = {};
    Object.keys(values.specialityRates || {}).forEach(specId => {
      if (selectedSpecialtyIds.includes(parseInt(specId))) {
        filteredRates[specId] = values.specialityRates[specId];
      }
    });

    const transformedRates = transformSpecialityRatesForAPI(filteredRates);

    const cleanTitles = Array.isArray(values.title) 
      ? values.title.map(t => t.typeId || t) 
      : [];
    const cleanLangs = Array.isArray(values.languages) 
      ? values.languages.map(l => l.typeId || l) 
      : [];
    const cleanSpecs = currentSpecialties.map(s => s.id || s.typeId || s);

    const trimmedUserName = values.userName?.trim() ?? "";
    if (!trimmedUserName) {
      setError("userName", {
        type: "manual",
        message: t("validation:required-field"),
      }, { shouldFocus: true });
      return;
    }

    const isUsernameValid = validateUsername(trimmedUserName);

    if (!isUsernameValid) {
      setIsUsernameInvalid(true);
      setError("userName", {
        type: "manual",
        message: "",
      }, { shouldFocus: true });
      return;
    }

    startLoading({ btnName: "update" });

    const doctorData = {
      ...values,
      id: data.id,
      userName: trimmedUserName,
      speciality: cleanSpecs,
      specialityRates: transformedRates, 
      title: cleanTitles,
      languages: cleanLangs,
      universitySpeciality: values.universitySpeciality || [],
      university: values.university || [],
      SpecialityNumber: values.SpecialityNumber || [],
      isPasswordReset: false,
      language: state.lang.code,
      currencyCode: values.currencyCode || "LKR",
      profilePicture: values.profileImage instanceof File ? values.profileImage : null,
      signature: values.signature instanceof File ? values.signature : null,
    };

    try {
      const d = await updateDoctor({ doctor: doctorData });

      if (d.status === "Success") {
        setOriginalSpecialtiesState({
          specialties: JSON.parse(JSON.stringify(currentSpecialties)),
          rates: JSON.parse(JSON.stringify(currentRates))
        });
        reset({ ...values, userName: trimmedUserName });
        loadDoctors();
        closeMain();
      } else {
        const errorMessages = [
          d?.message,
          ...(Array.isArray(d?.data) ? d.data : []),
        ].filter((item) => typeof item === "string" && item);

        const errorMessage = errorMessages.join(" ");

        const userNameError = errorMessages.find(isUserNameDuplicateError);

        const emailErrorItem = errorMessages.find((message) =>
          message === "email-exist" || message === "admin-email-exist" || message.includes("Email")
        );

        const phoneErrorItem = errorMessages.find((message) =>
          message === "phone-exist" || message === "admin-phone-exist"
        );

        if (errorMessage.includes("Cannot remove")) {
          const cleanMsg = t("validation:specialty-have-future-session");

          setValue("speciality", [...originalSpecialtiesState.specialties], {
            shouldValidate: false,
          });
          setValue("specialityRates", { ...originalSpecialtiesState.rates }, {
            shouldValidate: false,
          });

          setError(
            "speciality",
            {
              type: "manual",
              message: cleanMsg,
            },
            { shouldFocus: true },
          );
        } else {
          let hasFieldError = false;

          if (emailErrorItem) {
            const emailErrorKey = (emailErrorItem === "email-exist" || emailErrorItem === "admin-email-exist") 
              ? `validation:${emailErrorItem}` 
              : "validation:email-exist";
            setError("email", {
              type: "manual",
              message: t(emailErrorKey),
            });
            hasFieldError = true;
          }

          if (phoneErrorItem) {
            const phoneErrorKey = phoneErrorItem === "admin-phone-exist" ? "validation:admin-phone-exist" : "validation:phone-exist";
            setError("phoneNumber", {
              type: "manual",
              message: t(phoneErrorKey),
            });
            hasFieldError = true;
          }

          if (userNameError) {
            setError("userName", {
              type: "manual",
              message: t(USER_NAME_ALREADY_EXISTS_VALIDATION_KEY),
            });
            hasFieldError = true;
          }

          if (!hasFieldError) {
            setErrors({ visible: true, type: "server-error" });
          }
        }
      }
    } catch (error) {
      setErrors({ visible: true, type: "server-error" });
    } finally {
      stopLoading();
    }
  };

    const transformSpecialityRatesForAPI = (specialityRatesObject) => {
    const transformedRates = [];
    
    if (!specialityRatesObject || typeof specialityRatesObject !== 'object') {
      return transformedRates;
    }

    Object.entries(specialityRatesObject).forEach(([specialityId, rates]) => {
      const onlinePrice = rates.onlinePrice;
      const physicalPrice = rates.physicalPrice;
      const patientOnlinePrice = rates.patientOnlinePrice;
      const patientPhysicalPrice = rates.patientPhysicalPrice;

      // Online Rate transformation
      const hasOnlinePrice = onlinePrice !== "" && onlinePrice !== null && onlinePrice !== undefined;
      const hasPatientOnlinePrice = patientOnlinePrice !== "" && patientOnlinePrice !== null && patientOnlinePrice !== undefined;

      if (hasOnlinePrice || hasPatientOnlinePrice) {
        const parsed = hasOnlinePrice ? parseFloat(onlinePrice) : 0;
        const patientParsed = hasPatientOnlinePrice ? parseFloat(patientOnlinePrice) : 0;
        
        if ((!isNaN(parsed) && parsed >= 0) || (!isNaN(patientParsed) && patientParsed >= 0)) { 
          transformedRates.push({
            specialityId: parseInt(specialityId),
            isOnline: true,
            isPhysical: false,
            price: isNaN(parsed) ? 0 : parsed,
            patientPrice: isNaN(patientParsed) ? 0 : patientParsed
          });
        }
      }
      
      // Physical Rate transformation
      const hasPhysicalPrice = physicalPrice !== "" && physicalPrice !== null && physicalPrice !== undefined;
      const hasPatientPhysicalPrice = patientPhysicalPrice !== "" && patientPhysicalPrice !== null && patientPhysicalPrice !== undefined;

      if (hasPhysicalPrice || hasPatientPhysicalPrice) {
        const parsed = hasPhysicalPrice ? parseFloat(physicalPrice) : 0;
        const patientParsed = hasPatientPhysicalPrice ? parseFloat(patientPhysicalPrice) : 0;
        
        if ((!isNaN(parsed) && parsed >= 0) || (!isNaN(patientParsed) && patientParsed >= 0)) { 
          transformedRates.push({
            specialityId: parseInt(specialityId),
            isOnline: false,
            isPhysical: true,
            price: isNaN(parsed) ? 0 : parsed,
            patientPrice: isNaN(patientParsed) ? 0 : patientParsed
          });
        }
      }
    });

    return transformedRates;
    };
  // create Profile Handler
  const createProfileHandler = async (values) => {
    if (errors.visible) {
      setErrors({ visible: false, type: undefined });
    }

    const trimmedUserName = values.userName?.trim() ?? "";
    if (!trimmedUserName) {
      setError("userName", {
        type: "manual",
        message: t("validation:required-field"),
      }, { shouldFocus: true });
      return;
    }

    const isUsernameValid = validateUsername(trimmedUserName);

    if (!isUsernameValid) {
      setIsUsernameInvalid(true);
      setError("userName", {
        type: "manual",
        message: "",
      }, { shouldFocus: true });
      return;
    }

    startLoading({ btnName: "create" });

     const selectedSpecialtyIds = (values.speciality || []).map(s => s.id || s.typeId);
  
    const filteredRates = {};
    Object.keys(values.specialityRates || {}).forEach(specId => {
      if (selectedSpecialtyIds.includes(parseInt(specId))) {
        filteredRates[specId] = values.specialityRates[specId];
      }
    });


    // Pre-process values without mutating the form state
    const cleanValues = { ...values };
    
    cleanValues.hourlyRate = cleanValues.hourlyRate <= "" ? 0 : cleanValues.hourlyRate;
    cleanValues.onsiteHourlyRate = cleanValues.onsiteHourlyRate <= "" ? 0 : cleanValues.onsiteHourlyRate;
    cleanValues.speciality = specialityCodeHandler(cleanValues.speciality);
    if (
      Array.isArray(cleanValues.title) &&
      cleanValues.title.length > 0 &&
      cleanValues.title[0] !== ""
    ) {
      cleanValues.title = titleHandler(cleanValues.title);
    }

    if (cleanValues.languages && cleanValues.languages.length > 0) {
      cleanValues.languages = languageHandler(cleanValues.languages);
    }

    const transformedRates = transformSpecialityRatesForAPI(cleanValues.specialityRates);
    // Separate images from the rest of the form data
    const { signature, profilePicture, specialityRates, ...finalCleanValues } = cleanValues;

    // Compose the doctor object for the API
    const doctorData = {
      ...finalCleanValues,
      userName: trimmedUserName,
      specialityRates: transformedRates,
      SpecialityNumber: values.SpecialityNumber || [],
      isPasswordReset: false,
      language: state.lang.code,
      currencyCode: values.currencyCode || "LKR",
      profilePicture, // File object or null
      signature, // File object or null
    };

    await createDoctor({ doctor: doctorData }).then((d) => {
      if (d.type === 3 && d?.message === "server-error") {
        setErrors({ visible: true, type: d?.message });
        return;
      }
      if (d?.data && d?.data[0] === "server-error") {
        setErrors({ visible: true, type: d?.data[0] });
        return;
      }
      if (d?.status === "Success" && d?.data?.[1] == "email-sent") {
        openOverlay({
          OverlayName: "reset-password",
          mode: "email-sent",
        });
      } else if (d?.status === "Error" && d?.data?.[0] == "email-not-sent") {
        openOverlay({
          OverlayName: "reset-password",
          mode: "fail-email-not-sent",
        });
      } else if (d?.status == "Error") {
        values.hourlyRate = values.hourlyRate <= 0 ? "" : values.hourlyRate;

        if (d && d.data && d.data[0] !== null) {
          const emailErrorKey = d.data[0] === "admin-email-exist" ? "validation:admin-email-exist" : "validation:email-exist";
          setError("email", {
            type: "focus",
            message: t(emailErrorKey),
          });
        }
        if (d && d.data && d.data[1] !== null && d.data[1] !== undefined) {
          const phoneErrorKey = d.data[1] === "admin-phone-exist" ? "validation:admin-phone-exist" : "validation:phone-exist";
          setError("phoneNumber", {
            type: "focus",
            message: t(phoneErrorKey),
          });
        }
        if (d && d.data && d?.data[3] !== null) {
          setError("professionalLiceneseNumber", {
            type: "focus",
            message: t("validation:doctor-prof-license-exist"),
          });
        }
        if (d && d.data && d.data[4] !== null) {
          setError("registrationNo", {
            type: "focus",
            message: t("validation:doctor-reg-exist"),
          });
        }
        if (d && d.data && d?.data[5]) {
          setError("SpecialityNumber", {
            type: "focus",
            message: t("validation:doctor-speciality-no-exist"),
          });
        }
        if (d && d.data && d?.data[6] != 0) {
          setError("hourlyRate", {
            type: "focus",
            message: t("validation:maxium-hourley-exceed"),
          });
        }
        if (d && d.data && d?.data[7] != 0) {
          setError("onsiteHourlyRate", {
            type: "focus",
            message: t("validation:maxium-hourley-exceed"),
          });
        }
        const userNameError = Array.isArray(d?.data)
          ? d.data.find(isUserNameDuplicateError)
          : isUserNameDuplicateError(d?.message)
          ? d.message
          : null;
        if (userNameError) {
          setError("userName", {
            type: "focus",
            message: t(USER_NAME_ALREADY_EXISTS_VALIDATION_KEY),
          });
        }
      }

      values.hourlyRate = values.hourlyRate <= 0 ? "" : values.hourlyRate;
      values.onsiteHourlyRate = values.onsiteHourlyRate <= 0 ? "" : values.onsiteHourlyRate;
    });

    stopLoading();
  };

  const getSpecialityValues = async () => {
    try {
      startLoading({ btnName: "default" });
      const response = await GetDoctorSpecialization(state.lang.code);

      if (response.type == 0) {
        setDoctorMasterData((prev) => {
          return {
            ...prev,
            selectedSpeciality: watch().speciality,
            data: response.data?.data,
            hourRate: response?.data.hourRate?.value,
            onsiteHourRate: response?.data.onsiteHourRate?.value,
          };
        });
      } else {
      }
      if (mode != "update") {
        stopLoading({ btnName: "default" });
      }
    } catch (exception) {}
  };

  const getLanguageList = async () => {
    try {
      const response = await getLanguages("en");
      setLanguageList(response?.data ?? []);
    } catch (error) {
      console.error("Error fetching languages:", error);
    }
  };

  const getTitleList = async () => {
    try {
      const response = await getTitles("en");
      setTitleList(response?.data ?? []);
    } catch (error) {
      console.error("Error fetching titles:", error);
    }
  };

  const qualificationHandler = (qualification) => {
    let qualificationHandler = [];
    qualification.map((item) => {
      qualificationHandler.push(item?.qualification.toString());
    });

    return qualificationHandler;
  };

  const universityHandler = (university) => {
    if (Array.isArray(university)) {
      return university?.map((item) => item?.university?.toString());
    } else {
      return [];
    }
  };

  const specialityNumberHandler = (specialityNumber) => {
    if (Array.isArray(specialityNumber)) {
      return specialityNumber?.map((item) =>
        item?.specialityNumber?.toString(),
      );
    } else {
      return [];
    }
  };

  const specialityCodeHandler = (speciality) => {
    let specialityArranged = [];

    if (Array.isArray(speciality)) {
      speciality?.map((item) => {
        const specId = item.id || item.typeId;
        specialityArranged.push(specId.toString());
      });
    }

    return specialityArranged;
  };

  const languageHandler = (langauge) => {
    let langaugeArranged = [];

    if (Array.isArray(langauge)) {
      langauge?.map((item) => {
        langaugeArranged.push(item.typeId.toString());
      });
    }

    return langaugeArranged;
  };

  const titleHandler = (title) => {
    let titleArranged = [];

    if (Array.isArray(title)) {
      title?.map((item) => {
        titleArranged.push(item.typeId.toString());
      });
    }

    return titleArranged;
  };

  useEffect(() => {
    getSpecialityValues();
    getLanguageList();
    getTitleList();
    if (mode == "update") {
      startLoading({ btnName: "default" });
      getDoctorsByID();
    }
  }, [mode]);



  useEffect(() => {
    const subscription = watch((value, { name }) => {
      
      if (name === "speciality") {
        const currentSpecialties = value.speciality || [];
        const currentRates = value.specialityRates || {};
        
        const currentIds = currentSpecialties.map(s => s.id);
        const existingRateIds = Object.keys(currentRates).map(id => parseInt(id));

    
        const removedIds = existingRateIds.filter(id => !currentIds.includes(id));

        if (removedIds.length > 0) {
          removedIds.forEach(id => {
            unregister(`specialityRates.${id}`);
          });


          const cleanedRates = {};
          currentIds.forEach(id => {
            if (currentRates[id]) cleanedRates[id] = currentRates[id];
          });
          setValue("specialityRates", cleanedRates, { shouldDirty: true });
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]); 

  return (
    <SideDrawer
      isLoading={LoadingState.visible}
      headingText={
        mode == "update"
          ? t("glossary:add-doctor.updatedoctor")
          : t("glossary:add-doctor.new-doctor-title")
      }
      width={"690px"}
      openDrawer={OpenDrawer}
      close={mode == "update" ? closeMain : close}
      anchorDrawer={"right"}
    >
      <div
        style={{
          paddingBottom: "20px",
        }}
      >
        {errors.visible && errors.type === "server-error" && (
          <NoRecordsFound
            isError={errors.visible}
            textPosition={"start"}
            padding={"8px"}
            textColor={"black"}
            label={t("validation:server-error")}
            mB={"8px"}
          />
        )}
        <form
          onSubmit={handleSubmit(
            (data) => {
              if (mode === "update") {
                updateProfileHandler(data);
              } else {
                createProfileHandler(data);
              }
            },
            (validationErrors) => {
              // Auto switch to the tab that has errors so user can see them
              const rateErrors = validationErrors?.specialityRates;
              if (rateErrors && typeof rateErrors === 'object') {
                const hasPatientError = Object.values(rateErrors).some(
                  (r) => r?.patientOnlinePrice || r?.patientPhysicalPrice
                );
                const hasDoctorError = Object.values(rateErrors).some(
                  (r) => r?.onlinePrice || r?.physicalPrice
                );
                // Switch to patient tab if patient has errors but doctor doesn't
                if (hasPatientError && !hasDoctorError) {
                  setAppType("patient");
                } else {
                  setAppType("doctor");
                }
              }
            }
          )}
          onChange={() => {
            if (errors.visible) {
              setErrors({ visible: false, type: undefined });
            }
            if (isUsernameInvalid) {
              setIsUsernameInvalid(false);
            }
          }}
        >
          <Stack rowGap={"20px"} width={"640px"}>
            {/* Profile Picture Upload */}
            <ProfilePictureUpload
              control={control}
              setValue={setValue}
              watch={watch}
              setError={setError}
            />

            {/* Title Dropdown - Updated to use Multicomplete */}
            <Multicomplete
              options={titleList}
              control={control}
              limitTags={2}
              label={"Title"}
              required={false}
              name={"title"}
              menuItemKey={"typeId"}
              menuItemLabel={"abbreviation"}
              getOptionLabel={(option) => option?.abbreviation}
              value={watch().title ?? ""}
            />

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2,1fr)",
                gap: "16px",
              }}
            >
              <TextInput
                required={true}
                name={"firstName"}
                control={control}
                label={t("common:form.fname")}
              />
              <TextInput
                required={true}
                width={"100%"}
                name={"lastName"}
                control={control}
                label={t("common:form.lname")}
              />
              
              <Box 
                position="relative" 
                onFocusCapture={() => setIsUsernameFocused(true)}
                onBlurCapture={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget)) {
                    setIsUsernameFocused(false);
                    setIsInfoIconClicked(false);
                  }
                }}
              >
                  <TextInput
                    required={false}
                    name={"userName"}
                    control={control}
                    label={
                      <Box display="inline-flex" alignItems="center">
                        <span>{t("glossary:doctor-details.user-name")}</span>
                        <span style={{ color: "#FF0000", marginLeft: "4px", marginRight: "6px" }}>*</span>
                        <InfoIconGrey 
                          style={{ width: "16px", height: "16px", cursor: "pointer" }} 
                          onClick={(e) => {
                            e.preventDefault();
                            setIsInfoIconClicked((prev) => !prev);
                          }}
                        />
                      </Box>
                    }
                    maxLength={30}
                  />
                
                <ValidationPopper open={showUsernameRules} value={currentUserName} absolute={true} />
              </Box>

              <TextInput
                required={true}
                name={"email"}
                placeholder={t("common:placeholder.email")}
                control={control}
                label={t("common:form.email")}
                disabled={
                  watch().isPasswordReset && mode === "update"
                    ? watch().isPasswordReset
                    : undefined
                }
              />
              <PhoneNumber
                values={watch()}
                setValue={setValue}
                required={true}
                width={"220px"}
                name={"phoneNumber"}
                placeholder={"XX XXXX XXXX"}
                control={control}
                label={t("common:form.mobileNoFirstOnly")}
              />
            </div>
            <div>
              <SubHeader6 text={t("common:form.address")} />
              <Divider
                component={"div"}
                style={{
                  marginTop: "6px",
                }}
              />
            </div>
            <FormItemsWrap direction={"column"}>
              <TextInput
                name={"street01"}
                label={t("glossary:add-doctor.streetone")}
                control={control}
              />
              <TextInput
                name={"street02"}
                label={t("glossary:add-doctor.streettwo")}
                control={control}
              />
              <FormItemsWrap
                justifyContent={"space-between"}
                gap={"16px"}
                direction={"row"}
              >
                <TextInput
                  name={"city"}
                  label={t("glossary:add-doctor.city")}
                  control={control}
                />
                <TextInput
                  name={"region"}
                  label={t("glossary:add-doctor.region")}
                  control={control}
                />
                <TextInput
                  name={"state"}
                  label={t("glossary:add-doctor.state")}
                  control={control}
                />
              </FormItemsWrap>
              <TextInput
                name={"postCode"}
                label={t("glossary:add-doctor.postCode")}
                control={control}
              />
              <TextInput
                name={"country"}
                label={t("glossary:add-doctor.country")}
                control={control}
              />
            </FormItemsWrap>

            <FormItemsWrap direction={"column"} gap={"16px"}>
              <TextInput
                name={"professionalLiceneseNumber"}
                control={control}
                label={t("glossary:doctor-details:prof-license-no")}
              />
              <TextInput
                name={"registrationNo"}
                control={control}
                label={t("glossary:doctor-details:registration-no")}
              />
              <TagsAutocomplete
                control={control}
                fullWidth
                variant="outlined"
                id="university"
                name="university"
                label={t("glossary:doctor-details:university")}
                sublabel={`Please click the "Enter" key to add values`}
              />
              <TagsAutocomplete
                control={control}
                fullWidth
                variant="outlined"
                id="SpecialityNumber"
                sublabel={`Please click the "Enter" key to add values`}
                name="SpecialityNumber"
                label={t("glossary:doctor-details:specialty-no")}
              />
              <TagsAutocomplete
                control={control}
                fullWidth
                variant="outlined"
                id="universitySpeciality"
                sublabel={`Please click the "Enter" key to add values`}
                name="universitySpeciality"
                label={t("glossary:doctor-details:uni-specialty")}
              />
            </FormItemsWrap>

            <Multicomplete
                control={control}
                name={"speciality"}
                placeholder={t("common:placeholder.select")}
                label={t("glossary:add-doctor.specialty")}
                options={doctorMasterData.data.map(opt => ({
                  ...opt,
                  id: opt.id || opt.typeId
                }))}
                menuItemKey={"id"}
                menuItemLabel={"description"}
                value={watch().speciality ? watch().speciality : []}
                getOptionLabel={(option) => option?.description}
                required={true}
              />


                {/* Specialty Fee Configuration Table */}

<Box sx={{ mt: 2 }}>
  {watch("speciality")?.length > 0 && (
    <>
      <Typography variant="h6" sx={{ fontSize: '14px', fontWeight: 400, mb: 2 }}>
        {t("glossary:add-doctor.fee-configuration")}
      </Typography>

      <FormItemsWrap height={"auto"} sx={{ mt: 1 }}>
        <Select
          value={appType}
          onChange={(e) => setAppType(e.target.value)}
          IconComponent={ArrowDown}
          variant="outlined"
          fullWidth
          displayEmpty
          style={{ fontSize: "13px" }}
          sx={{
            "& .MuiSelect-icon": {
              marginRight: "11px",
              width: "16px",
              height: "16px",
              top: "calc(50% - 8px)",
            },
          }}
        >
          <MenuItem value="doctor" style={{ fontSize: "13px" }}>Doctor App</MenuItem>
          <MenuItem value="patient" style={{ fontSize: "13px" }}>Patient App</MenuItem>
        </Select>
      </FormItemsWrap>

      <Box sx={{ mt: 2 }}>
        <ConsultationFeeTable
          isEdit={true}
          control={control}
          speciality={watch("speciality")}
          appType={appType}
        />
      </Box>

      <Stack direction="column" spacing={0.5} sx={{ mt: 2 }}>
        <SubHeader2
          icon={<Info />}
          text={`${t("glossary:add-doctor:maximumhourlyrate")} (Online) - Rs.${doctorMasterData.hourRate}`}
        />
        <SubHeader2
          icon={<Info />}
          text={`${t("glossary:add-doctor:maximumhourlyrate")} (Walk-in) - Rs.${doctorMasterData.onsiteHourRate}`}
        />
      </Stack>
    </>
  )}
</Box>
          
            <Divider sx={{ my: 1, mb: 2 }} />
            <TextArea2
              showCharacterLength={true}
              minRows={1}
              noColor={true}
              control={control}
              label="Tagline"
              required={false}
              name={"tagline"}
              maxLength={100}
            />

            <TextArea2
              showCharacterLength={true}
              minRows={2}
              noColor={true}
              control={control}
              label="Biography"
              required={false}
              name={"biography"}
              maxLength={1024}
            />

            <TextInput
              required={false}
              width={"100%"}
              name={"experience"}
              control={control}
              label={"Experience (duration in months/years)"}
            />

            {/* Languages Multicomplete - Added from ProfileForm */}
            <Multicomplete
              options={languageList}
              control={control}
              limitTags={3}
              label={"Languages"}
              name={"languages"}
              menuItemKey={"typeId"}
              menuItemLabel={"languageName"}
              getOptionLabel={(option) => option.languageName}
              value={watch().languages ?? []}
            />

            <TagsAutocomplete
              control={control}
              fullWidth
              variant="outlined"
              id="treatment"
              name="treatment"
              label={"Treatment Interests"}
              sublabel={`Please click the "Enter" key to add values`}
              value={watch().treatment ?? []}
            />
            {/* Signature Upload Component */}
            <SignatureUpload
              control={control}
              setValue={setValue}
              watch={watch}
              setError={setError}
            />

            <FormItemsWrap
              direction={"row"}
              justifyContent={"flex-end"}
              gap={"16px"}
            >
              <Button
                onClick={mode == "update" ? closeMain : close}
                variant="whiteSquare"
              >
                {t("common:button.cancel")}
              </Button>
              <LoadingButtonType
                variant="primaryBlue"
                type="submit"
                loading={LoadingState.visible}
              >
                {mode
                  ? t("glossary:add-doctor.updateprofile")
                  : t("glossary:add-doctor.createprofile")}
              </LoadingButtonType>
            </FormItemsWrap>
          </Stack>
        </form>
      </div>
      {OverlayState.visible && (
        <ConfirmDialog
          isError={OverlayState.mode === "fail-email-not-sent"}
          title={t("user.action.reset-password", { ns: "glossary" })}
          description={
            OverlayState.mode === "fail-email-not-sent"
              ? t("validation:reset-password-link-send-fail")
              : t("user.action.reset-password-desc", { ns: "glossary" })
          }
          open={OverlayState?.visible}
          close={() => {
            if (OverlayState.mode !== "fail") {
              close();
            }

            closeOverlay();
          }}
          onOkClick={() => {
            if (OverlayState.mode !== "fail") {
              reset();
              loadDoctors();
              close();
            }
            closeOverlay();
          }}
        />
      )}
    </SideDrawer>
  );
};

export default AddDoctor;
